import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck, X } from "lucide-react";
import { Link } from "../router";
import { api, apiErrorText, endpoints, withQuery } from "../lib/api";
import { idOf, listFrom, text } from "../lib/data";
import { formatDateTime } from "../lib/format";
import { EmptyState, ErrorState, LoadingState, SafeText } from "./ui";

export function NotificationsPanel({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["notifications", "recent"],
    queryFn: () => api.get<unknown>(withQuery(endpoints.notifications, { limit: 20 })),
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${encodeURIComponent(id)}/read`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const items = listFrom(query.data, "notifications");

  return (
    <aside className="notification-panel" aria-label="Benachrichtigungen">
      <header>
        <div>
          <span className="eyebrow">Inbox</span>
          <h2>Benachrichtigungen</h2>
        </div>
        <button
          className="icon-button"
          type="button"
          onClick={onClose}
          aria-label="Benachrichtigungen schließen"
        >
          <X aria-hidden="true" />
        </button>
      </header>
      <div className="notification-panel__body">
        {query.isPending && <LoadingState rows={3} />}
        {query.isError && (
          <ErrorState message={apiErrorText(query.error)} onRetry={() => void query.refetch()} />
        )}
        {query.isSuccess && items.length === 0 && (
          <EmptyState title="Alles gelesen" message="Neue Hinweise erscheinen hier in Echtzeit." />
        )}
        {items.map((item) => {
          const id = idOf(item);
          const unread = item.readAt === null || item.isRead === false;
          const target = text(item.targetUrl ?? item.href, "");
          const content = (
            <>
              <span className={`notification-item__icon${unread ? " is-unread" : ""}`}>
                <Bell aria-hidden="true" size={16} />
              </span>
              <span>
                <strong>
                  <SafeText>{text(item.title, "Neue Benachrichtigung")}</SafeText>
                </strong>
                <small>
                  <SafeText>{text(item.message ?? item.body, "")}</SafeText>
                </small>
                <time>{formatDateTime(item.createdAt)}</time>
              </span>
            </>
          );
          return (
            <article
              className="notification-item"
              key={id || `${String(item.createdAt)}-${text(item.title)}`}
            >
              {target.startsWith("/") ? (
                <Link to={target} onClick={onClose}>
                  {content}
                </Link>
              ) : (
                <div>{content}</div>
              )}
              {unread && id && (
                <button
                  className="icon-button"
                  type="button"
                  onClick={() => markRead.mutate(id)}
                  disabled={markRead.isPending}
                  aria-label="Als gelesen markieren"
                >
                  <CheckCheck aria-hidden="true" size={16} />
                </button>
              )}
            </article>
          );
        })}
      </div>
      <Link
        className="button button--ghost button--full"
        to="/account/benachrichtigungen"
        onClick={onClose}
      >
        Alle Einstellungen
      </Link>
    </aside>
  );
}
